import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { clearAccountScopedStorage } from '@/lib/accountScopedStorage';

/**
 * Gives every business account a fresh UI state.
 *
 * Pages never unmount when the active business account changes (switching is
 * just a context state update), so without this boundary every page-local
 * `useState` (search text, status filters, date ranges, sort, pagination,
 * selected tabs/rows) and every URL search param (`?search=`, `?filter=`,
 * `?tab=`, `?page=`, ...) survives the switch and leaks Account A's filters
 * into Account B.
 *
 * This component is the single, centralized reset. On a REAL account switch
 * (previous scope was a concrete account and the id changed) it:
 *  1. unmounts the old page tree immediately (renders null for one tick),
 *  2. wipes transient account-scoped localStorage handoffs,
 *  3. clears the URL query string / hash via `replace` navigation
 *     (no history entry is added, so Back still returns to the previous page),
 *  4. remounts the page tree under a `key` derived from the new account id.
 *
 * The remount-after-URL-clear ordering matters: a freshly mounted page must
 * never seed its filters from the previous account's query string.
 *
 * What it deliberately does NOT do:
 * - Initial mount / page refresh / deep links: the first adoption of an
 *   account id (including the fresh-login auto-select from `null`) never
 *   touches the URL, so shared filter links and refreshes keep their params.
 * - Admin routes (no BusinessAccountProvider): `selectedAccountId` is always
 *   null there, so the boundary is inert.
 * - React Query cache: query keys already include the business account id,
 *   so cached data is naturally partitioned per account; wiping it would only
 *   slow down switching back. The remount alone kills `keepPreviousData`
 *   carry-over because observers are recreated.
 */
export const AccountScopeBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedAccountId } = useBusinessAccountContext();
  const location = useLocation();
  const navigate = useNavigate();

  // The account id the currently rendered page tree belongs to.
  const [scopeId, setScopeId] = React.useState<string | null>(selectedAccountId);
  const isSwitching = selectedAccountId !== scopeId;

  // Location key of the last render committed for the CURRENT scope. When a
  // handler calls setSelectedAccountId AND navigate in the same event (e.g.
  // clicking a notification that belongs to another account deep-links to
  // `/home/inbox?conversationId=...`), both updates land in one commit — the
  // destination URL/state was built FOR the new account, so the boundary must
  // not strip it. A changed location.key on the switch commit detects this.
  const preSwitchLocationKeyRef = React.useRef(location.key);
  if (!isSwitching) {
    preSwitchLocationKeyRef.current = location.key;
  }

  React.useLayoutEffect(() => {
    if (!isSwitching) return;

    const navigatedWithSwitch = location.key !== preSwitchLocationKeyRef.current;

    // scopeId === null means this is the initial adoption of an account
    // (fresh login auto-select) — not a user-driven switch. Adopt silently
    // so deep links with query params survive first load. Likewise, when a
    // navigation accompanied the switch, the whole destination (query params,
    // location.state, staged storage handoffs) is an intentional handoff into
    // the new account — leave it intact.
    if (scopeId !== null && !navigatedWithSwitch) {
      clearAccountScopedStorage();
      if (location.search || location.hash) {
        // Strip the old account's query params/hash first; adopt the new
        // scope on the next effect pass, once the URL is clean, so the
        // remounted page can never read the old account's params.
        navigate(location.pathname, { replace: true });
        return;
      }
    }
    setScopeId(selectedAccountId);
  }, [isSwitching, scopeId, selectedAccountId, location, navigate]);

  // Unmount the old account's page tree immediately while the URL is being
  // cleared. useLayoutEffect above runs before paint, so the gap is not a
  // visible blank frame.
  if (isSwitching) return null;

  // Keying by scope id forces a full remount of the routed page subtree on
  // every account change — all page-local state reinitializes. The key is
  // stable otherwise, so this adds zero re-renders in steady state.
  return <React.Fragment key={scopeId ?? 'no-account'}>{children}</React.Fragment>;
};

export default AccountScopeBoundary;
